import type { HTMLAttributes, ReactNode } from "react";

type SplitPaneFirstWidth = "narrow" | "half";

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

export interface SplitPaneProps extends HTMLAttributes<HTMLDivElement> {
  /** Accessible name for the two-pane group (exposed as `role="group"`). */
  ariaLabel?: string;
  /** Content of the first (left / rollup-summary) pane. */
  first: ReactNode;
  /**
   * Fixed column ratio for the first pane: `narrow` ≈ 2:3 (default),
   * `half` = 1:1. Closed variants only — no drag-resize (UI-LAW §2.9).
   */
  firstWidth?: SplitPaneFirstWidth;
  /** Content of the second (right / detail) pane. */
  second: ReactNode;
}

/**
 * The Workspace-archetype two-pane layout (UI-LAW §4c). A full-bleed grid at
 * a fixed ratio: each pane is its own scroll region, separated by a single
 * hairline (the first pane's border — no boxes, §2.1). No drag-resize and no
 * motion (§2.9); below 1024px the panes stack vertically and lose their fixed
 * heights. Pure CSS composition — no behavior beyond class names.
 */
export function SplitPane({
  ariaLabel,
  className,
  first,
  firstWidth = "narrow",
  role,
  second,
  ...props
}: SplitPaneProps) {
  return (
    <div
      aria-label={ariaLabel}
      className={classes(
        ["marker-split-pane", `marker-split-pane--${firstWidth}`],
        className,
      )}
      role={role ?? (ariaLabel === undefined ? undefined : "group")}
      {...props}
    >
      <div className="marker-split-pane__pane marker-split-pane__pane--first">
        {first}
      </div>
      <div className="marker-split-pane__pane marker-split-pane__pane--second">
        {second}
      </div>
    </div>
  );
}
